import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import { useSelector, shallowEqual } from 'react-redux';
import { useTracking } from 'react-tracking';
import selector from './selector';
import MultipleVsPeers from './components/MultipleVsPeers';
import MultipleVsIndustry from './components/MultipleVsIndustry';
import KeyValuationMetrics from './components/KeyValuationMetrics';
import SharePriceVsFairValue from './components/SharePriceVsFairValue';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import { CompanyStatements } from '@/constants/statementIds';
import Introduction from '@/pages/CompanyReport/components/Overview/components/IntroductionDeprecated';
import FairRatio from './components/FairRatio';
import type { FAQPage } from 'schema-dts';
import DocumentSchema from '@/components/DocumentSchema';
import PageAnchor from '@/components/PageAnchor';
import { useFlag } from '@/features/unleash';
import {
  VALUATION_KEY_METRICS,
  VALUATION_RATIO_VS_PEERS,
  VALUATION_RATIO_VS_INDUSTRY,
  VALUATION_FAIR_RATIO,
  VALUATION_PRICE_VS_FAIR_RATIO,
  VALUATION_HISTORICAL_TIMESERIES,
  ANALYST_PRICE_TARGETS,
} from '@/constants/anchors';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import loadable from '@loadable/component';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import IndustryContextProvider from './components/MultipleVsIndustry/components/IndustryContextProvider';
import { AnalystPriceTargets } from './components/AnalystPriceTargets';
import { VALUATION_STATEMENTS, generateQandA } from './utils';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getStatementChecks } from '../Introduction/utils';
import { useMemo } from 'react';
import { useMetricFullLabel } from './hooks/useLabel';
import { useIndustrySource } from './hooks/useIndustrySource';
import { TermTooltip } from './components/TermTooltip';
import {
  useValuationTranslation,
  ValuationTranslation,
} from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { getPassRewardList, getRiskList } from '../OverviewSection/utils';

const HistoricalReportSection = loadable(
  () => import('./components/HistoricalChart')
);

const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const NextStep = loadable(
  () => import('@/pages/CompanyReport/components/SimilarCompanies/NextStep')
);

interface Props {
  sectionNumber: number;
}

export const Valuation = ({ sectionNumber }: Props) => {
  const { Track } = useTracking({ subject: 'valuation' });
  const flag = useFlag({ name: 'mono-historical-values-pe' });
  const shouldShowHistoricalValuesChart = flag.flagsReady && flag.enabled;

  const isPristine = useVisibleSectionsIsPristine();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';

  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const statementChecks = useMemo(
    () => getStatementChecks(VALUATION_STATEMENTS, statements),
    [statements]
  );

  // Fetch company industry histogram data
  const { name: multipleName } = selected.multiples.preferredMultiple;

  const t = useValuationTranslation();

  const { industryName, industrySource } = useIndustrySource();

  const getTerm = useGlossaryTerms();
  const getMetricLabel = useMetricFullLabel();

  const riskList = getRiskList(statements);
  const passRewardsList = getPassRewardList(statements);

  const riskRewardFaqs = passRewardsList.map((passReward) => {
    return generateQandA(
      passReward.title.endsWith('?')
        ? passReward.title
        : `${passReward.title}?`,
      passReward.state === 'pass' ? t('faqYes') : t('faqNo')
    );
  });

  Object.values(riskList).forEach((risks) => {
    risks.forEach((risk) => {
      riskRewardFaqs.push(
        generateQandA(
          risk.title.endsWith('?') ? risk.title : `${risk.title}?`,
          risk.state === 'pass' ? t('faqYes') : t('faqNo')
        )
      );
    });
  });

  const totalScore = useMemo(() => {
    return statementChecks.filter((current) => current.mark === 'PASS').length;
  }, [statementChecks]);

  return (
    <Track>
      <ReportSection
        data-cy-id="valuation-section"
        sectionNumber={sectionNumber}
      >
        <DocumentSchema<FAQPage>
          item={{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              generateQandA(
                t('description', {
                  tickerSymbol: selected.tickerSymbol,
                }),
                `Valuation Score ${totalScore}/6`
              ),
              generateQandA(
                t('qanda.vsPeers', {
                  tickerSymbol: selected.tickerSymbol,
                  metric: multipleName,
                }),
                statements
                  ?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                  ?.description
              ),
              generateQandA(
                industrySource !== undefined
                  ? t('vsIndustry.description.withSource', {
                      tickerSymbol: selected.tickerSymbol,
                      metric: multipleName,
                      industrySource,
                      industryName,
                    })
                  : t('vsIndustry.description.withoutSource', {
                      tickerSymbol: selected.tickerSymbol,
                      metric: multipleName,
                      industryName,
                    }),
                statements?.IsGoodValueComparingPreferredMultipleToIndustry
                  ?.description
              ),
              generateQandA(
                t('qanda.fairRatio', {
                  tickerSymbol: selected.tickerSymbol,
                  metric: multipleName,
                }),
                statements?.IsGoodValueComparingRatioToFairRatio?.description
              ),
              generateQandA(
                t('qanda.vsFair', {
                  tickerSymbol: selected.tickerSymbol,
                }),
                statements?.IsUndervaluedBasedOnDCF?.description
              ),
              generateQandA(
                t('qanda.analystPriceTarget'),
                statements?.IsAnalystForecastTrustworthy?.description
              ),
            ],
          }}
        />

        <Introduction
          title={<CompanySectionTitle title={t('heading')} />}
          description={t('description', {
            tickerSymbol: selected.tickerSymbol,
          })}
          checks={statementChecks}
          section="valuation"
        />
        <BrazeContentCardWrapper>
          <BrazeContentCard
            feedType="company-overview.valuation"
            anchorTarget="_blank"
          />
        </BrazeContentCardWrapper>
        <PageAnchor
          type="sub-section-title"
          id={VALUATION_PRICE_VS_FAIR_RATIO}
        />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t('sharePrice.heading')}
          description={t('sharePrice.description', {
            tickerSymbol: selected.tickerSymbol,
          })}
          bodyRender={() => <SharePriceVsFairValue />}
          statements={
            <>
              <Statement
                title={statements?.IsUndervaluedBasedOnDCF?.title}
                description={
                  <Description
                    statement={statements?.IsUndervaluedBasedOnDCF}
                  />
                }
                value={statements?.IsUndervaluedBasedOnDCF?.value}
                statementId={CompanyStatements.valueStatement1}
              />
              <Statement
                title={statements?.IsHighlyUndervaluedBasedOnDCF?.title}
                description={
                  <Description
                    statement={statements?.IsHighlyUndervaluedBasedOnDCF}
                  />
                }
                value={statements?.IsHighlyUndervaluedBasedOnDCF?.value}
                statementId={CompanyStatements.valueStatement2}
              />
            </>
          }
        />
        <hr />
        <PageAnchor type="sub-section-title" id={VALUATION_KEY_METRICS} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t('keyMetric.heading')}
          description={t('keyMetric.description', {
            tickerSymbol: selected.tickerSymbol,
          })}
          bodyRender={() => <KeyValuationMetrics />}
        />
        <hr />
        <PageAnchor type="sub-section-title" id={VALUATION_RATIO_VS_PEERS} />
        <MultipleVsPeers
          preferredMultiple={multipleName}
          statements={
            <Statement
              title={
                statements
                  ?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                  ?.title
              }
              description={
                <Description
                  statement={
                    statements?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                  }
                />
              }
              value={
                statements
                  ?.IsGoodValueComparingPreferredMultipleToPeersAverageValue
                  ?.value
              }
              statementId={CompanyStatements.valuationStatement1}
            />
          }
        />
        <hr />
        {shouldShowHistoricalValuesChart && (
          <>
            <PageAnchor
              type="sub-section-title"
              id={VALUATION_HISTORICAL_TIMESERIES}
            />
            <HistoricalReportSection
              companyId={selected.companyId}
              subSectionTitleElement={subSectionTitleElement}
            />
            <hr />
          </>
        )}
        <PageAnchor type="sub-section-title" id={VALUATION_RATIO_VS_INDUSTRY} />
        <IndustryContextProvider>
          <MultipleVsIndustry
            preferredMultiple={multipleName}
            statements={
              <Statement
                title={
                  statements?.IsGoodValueComparingPreferredMultipleToIndustry
                    ?.title
                }
                description={
                  <Description
                    statement={
                      statements?.IsGoodValueComparingPreferredMultipleToIndustry
                    }
                  />
                }
                value={
                  statements?.IsGoodValueComparingPreferredMultipleToIndustry
                    ?.value
                }
                statementId={CompanyStatements.valuationStatement2}
              />
            }
          />
        </IndustryContextProvider>
        <hr />
        <PageAnchor type="sub-section-title" id={VALUATION_FAIR_RATIO} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t('vsFair.heading', {
            metricLabel: getMetricLabel(multipleName),
          })}
          description={
            <p>
              <ValuationTranslation
                i18nKey="vsFair.description"
                values={{
                  tickerSymbol: selected.tickerSymbol,
                  metric: multipleName,
                }}
              >
                <span>
                  What is {selected.tickerSymbol}'s {multipleName} Ratio
                  compared to its
                </span>
                <TermTooltip title={getTerm('fair ratio') ?? ''}>
                  Fair {multipleName} Ratio
                </TermTooltip>
                <span>
                  ? This is the expected {multipleName} Ratio taking into
                  account the company's forecast earnings growth, profit margins
                  and other risk factors.
                </span>
              </ValuationTranslation>
            </p>
          }
          bodyRender={() => <FairRatio />}
        />
        <hr />
        <PageAnchor type="sub-section-title" id={ANALYST_PRICE_TARGETS} />
        <ReportSubSection
          titleElement={subSectionTitleElement}
          title={t('analystPriceTarget.heading')}
          description={t('analystPriceTarget.description')}
          trackingProps={{ subject: 'chart' }}
          bodyRender={() => (
            <AnalystPriceTargets
              isDataTrustworthy={
                statements?.IsAnalystForecastTrustworthy?.value
              }
            />
          )}
          statements={
            <Statement
              title={statements?.IsAnalystForecastTrustworthy?.title}
              description={
                <Description
                  statement={statements?.IsAnalystForecastTrustworthy}
                />
              }
              value={statements?.IsAnalystForecastTrustworthy?.value}
              statementId={CompanyStatements.valuationStatement7}
            />
          }
        />
        <hr />
        <NextStep section="valuation" title={t('nextSteps.heading')} />
      </ReportSection>
    </Track>
  );
};
